import SectionHeader from '../../components/ui/section-header'
import { categoryInfo, systemConfiguration } from '../../data/system'
import CategoryListThead from '../../components/module/system-configuration/category-list-thead'
import CategoryListTBody from '../../components/module/system-configuration/category-list-tbody'
import { useDispatch, useSelector } from 'react-redux'
import { selectIsCategoryModalShow, selectSelectedTabs, setSelectedCategory } from '../../features/it-user-forms/user-forms-slice'
import CategoryModal from '../../components/module/system-configuration/modal/category-modal'
import { useMutation } from '../../hooks/services/mutations/use-mutation'
import { useCategoryActions } from '../../hooks/actions/use-category-actions'
import { useCategoryManagementUseMemo } from '../../hooks/memo/use-category-management.usememo'
import { useCategorySocket } from '../../hooks/socket/use-category-socket'
import TableError from '../../components/ui/table-error'
import TableLoading from '../../components/ui/table-loading'
import LoadingGlobal from '../../components/ui/loading-global'

export default function CategoryManagement() {

    const dispatch = useDispatch()
    const selectedTabs = useSelector(selectSelectedTabs)
    const isShowModal = useSelector(selectIsCategoryModalShow)
    const currentCategory = categoryInfo[selectedTabs];

    const { categoryData, isLoading, isError, errorMessage } = useCategoryManagementUseMemo()
    const { isLoadingCreateCategory, isLoadingUpdateCategory, } = useMutation()
    const isLoadingModal = isLoadingCreateCategory || isLoadingUpdateCategory
    const { handleShowModal, handleSelect } = useCategoryActions()
    const title = selectedTabs.slice(0, -1)
    const tabs: string[] = ['Status', `${title}`, 'Code', 'Action']

    useCategorySocket()

    return (
        <div className="flex h-full min-h-0 w-full flex-col bg-[#f8fafc] rounded-xl border border-gray-200">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <SectionHeader
                    title="System Configuration"
                    subtitle="Manage roles, departments, and locations used across the system."
                    icon={
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="m19.4 15 .1.1a2 2 0 0 1-2.8 2.8l-.1-.1a2 2 0 0 0-3.4 1.4V19a2 2 0 0 1-4 0v-.2A2 2 0 0 0 5.8 17l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a2 2 0 0 0-1.4-3.4H1.5a2 2 0 0 1 0-4h.2A2 2 0 0 0 3 3.4l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A2 2 0 0 0 9.2 1.5V1.3a2 2 0 0 1 4 0v.2A2 2 0 0 0 16.6 3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a2 2 0 0 0 1.4 3.4h.2a2 2 0 0 1 0 4h-.2a2 2 0 0 0-1.4 1.8Z" />
                        </svg>
                    }
                />
                {/* Content */}
                <div className="mx-auto flex min-h-0 w-full flex-1 flex-col gap-2 p-3">
                    {/* Tabs */}
                    <div className="overflow-x-auto scrollbar-hide">
                        <div className="flex min-w-max gap-1 rounded-xl border border-gray-200 bg-gray-100 p-1">
                            {systemConfiguration.map((tab) => {
                                const isActive = selectedTabs === tab.value;
                                return (
                                    <button
                                        key={tab.id}
                                        type="button"
                                        onClick={() => dispatch(setSelectedCategory(tab.value))}
                                        className={`group relative flex min-w-[110px] items-center justify-center rounded-lg px-4 py-2.5 text-xs font-semibold transition-all duration-200 sm:min-w-[130px] ${isActive ? "bg-white text-[#186cb2] shadow-sm ring-1 ring-gray-200" : "text-gray-500 hover:bg-white/70 hover:text-gray-700"}`}
                                    >
                                        <span className="flex items-center gap-2">
                                            <span className={`h-1.5 w-1.5 rounded-full transition-all duration-200 ${isActive ? "bg-[#186cb2]" : "bg-gray-300 group-hover:bg-gray-400"}`} />
                                            <span>{tab.name}</span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    {/* Card */}
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-b-md border border-gray-300 bg-white">
                        {/* Card Header */}
                        <div className="flex flex-col gap-4 border-b border-gray-100 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex min-w-0 items-center gap-3">
                                <div className="h-8 w-1 shrink-0 rounded-full bg-[#186cb2]" />
                                <div className="min-w-0">
                                    <h1 className="truncate text-sm font-semibold text-gray-800">
                                        {currentCategory.title}
                                    </h1>
                                    <p className="mt-0.5 truncate text-xs text-gray-500">
                                        {currentCategory.subtitle}
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                disabled={isLoading}
                                onClick={() => handleShowModal(currentCategory.value)}
                                className="flex w-full shrink-0 items-center justify-center gap-2 rounded-md border border-[#186cb2] bg-[#186cb2] px-3.5 py-2 text-xs font-medium text-white transition-all hover:bg-[#155b96] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-3.5 w-3.5" >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
                                </svg>
                                <span>{currentCategory.button}</span>
                            </button>
                        </div>
                        {/* Table */}
                        <div className="min-h-0 flex-1 overflow-auto">
                            {isLoading ? (
                                <TableLoading />
                            ) : isError ? (
                                <TableError description={errorMessage}/>
                            ) : (
                                <table className="w-full min-w-[600px] table-fixed">
                                    <CategoryListThead headers={tabs} />
                                    <CategoryListTBody
                                        data={categoryData[selectedTabs]}
                                        handleSelect={handleSelect}
                                    />
                                </table>
                            )}
                        </div>
                    </div>
                {/* </div> */}
                </div>
                {isShowModal && (<CategoryModal/>)}
                {isLoadingModal && (<LoadingGlobal/>)}
            </div>
        </div>
    );
}
